/**
 * Frontend Analytics Service
 * Tracks user interactions and sends events to the backend analytics API
 */

class FrontendAnalyticsService {
  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_URL || '';
    this.sessionId = this.getOrCreateSessionId();
    this.platform = 'web';
    this.userId = null;  // Set when user is authenticated
    this.deterministicViewTrackingEnabled = this.parseBooleanEnv(
      process.env.REACT_APP_ANALYTICS_DETERMINISTIC_VIEWS,
      false
    );

    // Track page entry times for duration calculation
    this.pageEntryTimes = new Map();
    this.sentViewExitKeys = new Set();
    this.lastExitFlushAt = 0;
    this.requestCounters = {
      standard: { success: 0, failure: 0 },
      unload: { success: 0, failure: 0 }
    };

    // Track page views automatically
    this.trackPageView();

    if (this.deterministicViewTrackingEnabled) {
      // Deterministic mode: explicit per-page exits, plus unload-safe flush fallback.
      window.addEventListener('beforeunload', () => {
        this.flushActiveViewExits(true);
      });
      window.addEventListener('pagehide', () => {
        this.flushActiveViewExits(true);
      });
    } else {
      // Legacy behavior kept for compatibility and rollback.
      window.addEventListener('beforeunload', () => {
        this.trackPageExits();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.trackPageExits();
        }
      });
      window.addEventListener('pagehide', () => {
        this.trackPageExits();
      });
    }
  }

  parseBooleanEnv(value, fallback = false) {
    if (value === undefined || value === null) return fallback;
    return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase());
  }

  generateSessionId() {
    return 'web_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  getOrCreateSessionId() {
    try {
      if (typeof sessionStorage === 'undefined') {
        return this.generateSessionId();
      }

      const storageKey = 'lilycove_analytics_session_id';
      const existing = sessionStorage.getItem(storageKey);
      if (existing) {
        return existing;
      }

      const created = this.generateSessionId();
      sessionStorage.setItem(storageKey, created);
      return created;
    } catch (_error) {
      return this.generateSessionId();
    }
  }

  // Call when user signs in/out to attach identity to events
  setUserId(userId) {
    this.userId = userId;
  }

  // Track when user enters a page/view
  startTimer(key) {
    this.pageEntryTimes.set(key, Date.now());
    this.sentViewExitKeys.delete(key);
  }

  // Calculate duration since timer started
  getTimerDuration(key) {
    const startTime = this.pageEntryTimes.get(key);
    if (!startTime) return 0;
    return Date.now() - startTime;
  }

  cleanParams(params = {}) {
    const cleaned = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  }

  buildQueryParams(params = {}) {
    return new URLSearchParams({
      ...this.cleanParams(params),
      session_id: this.sessionId,
      platform: this.platform,
      ...(this.userId ? { user_id: this.userId } : {})
    });
  }

  buildHeaders(includeJson = false) {
    const headers = {
      'X-Platform': this.platform,
      'X-Session-ID': this.sessionId
    };
    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }
    if (this.userId) {
      headers['X-User-ID'] = this.userId;
    }
    return headers;
  }

  incrementCounter(kind, success) {
    const bucket = this.requestCounters[kind] || this.requestCounters.standard;
    if (success) {
      bucket.success += 1;
    } else {
      bucket.failure += 1;
    }
  }

  shouldThrottleExitFlush() {
    const now = Date.now();
    if ((now - this.lastExitFlushAt) < 400) {
      return true;
    }
    this.lastExitFlushAt = now;
    return false;
  }

  // Track page exits to send final duration
  trackPageExits() {
    if (this.shouldThrottleExitFlush()) {
      return;
    }

    this.pageEntryTimes.forEach((startTime, key) => {
      const duration = Date.now() - startTime;
      if (duration > 1000) { // Only track if user spent more than 1 second
        console.log(`Page exit: ${key} duration: ${duration}ms`);
        
        // Send analytics for different types of views
        if (key.startsWith('card_')) {
          const cardId = key.replace('card_', '');
          this.trackEventQuery('track/card-view', {
            card_id: cardId,
            came_from: 'page_exit',
            view_duration_ms: duration
          });
          this.sentViewExitKeys.add(key);
        } else if (key.startsWith('set_')) {
          const setId = key.replace('set_', '');
          this.trackEventQuery('track/set-view', {
            set_id: setId,
            came_from: 'page_exit',
            view_duration_ms: duration
          });
          this.sentViewExitKeys.add(key);
        } else if (key.startsWith('series_')) {
          const seriesId = key.replace('series_', '');
          this.trackEventQuery('track/series-view', {
            series_id: seriesId,
            view_duration_ms: duration
          });
          this.sentViewExitKeys.add(key);
        }
      }
    });
    this.pageEntryTimes.clear();
  }

  flushActiveViewExits(useUnloadTransport = true) {
    if (this.shouldThrottleExitFlush()) {
      return;
    }

    this.pageEntryTimes.forEach((startTime, key) => {
      const duration = Date.now() - startTime;
      if (duration <= 1000 || this.sentViewExitKeys.has(key)) {
        return;
      }

      if (key.startsWith('card_')) {
        const cardId = key.replace('card_', '');
        this.trackEventQuery('track/card-view', {
          card_id: cardId,
          came_from: 'page_exit',
          view_duration_ms: duration
        }, { useUnloadTransport, transport: 'unload' });
      } else if (key.startsWith('set_')) {
        const setId = key.replace('set_', '');
        this.trackEventQuery('track/set-view', {
          set_id: setId,
          came_from: 'page_exit',
          view_duration_ms: duration
        }, { useUnloadTransport, transport: 'unload' });
      } else if (key.startsWith('series_')) {
        const seriesId = key.replace('series_', '');
        this.trackEventQuery('track/series-view', {
          series_id: seriesId,
          view_duration_ms: duration
        }, { useUnloadTransport, transport: 'unload' });
      }

      this.sentViewExitKeys.add(key);
      this.pageEntryTimes.delete(key);
    });
  }

  async trackEvent(endpoint, data = {}) {
    try {
      const headers = this.buildHeaders(true);

      const response = await fetch(`${this.apiBaseUrl}/api/analytics/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...data,
          session_id: this.sessionId,
          platform: this.platform,
          timestamp: new Date().toISOString(),
          ...(this.userId ? { user_id: this.userId } : {})
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      this.incrementCounter('standard', true);
      return await response.json();
    } catch (error) {
      // Don't throw errors for analytics - just log them
      console.warn('Analytics tracking failed:', error.message);
      this.incrementCounter('standard', false);
      return null;
    }
  }

  async trackEventQuery(endpoint, params = {}, options = {}) {
    const { useUnloadTransport = false, transport = 'default' } = options;
    const allParams = { ...params, transport };

    if (useUnloadTransport && this.deterministicViewTrackingEnabled) {
      return this.trackEventQueryUnload(endpoint, allParams);
    }

    try {
      const queryParams = this.buildQueryParams(allParams);
      const headers = this.buildHeaders(false);

      const response = await fetch(`${this.apiBaseUrl}/api/analytics/${endpoint}?${queryParams}`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      this.incrementCounter('standard', true);
      return await response.json();
    } catch (error) {
      // Don't throw errors for analytics - just log them
      console.warn('Analytics tracking failed:', error.message);
      this.incrementCounter('standard', false);
      return null;
    }
  }

  async trackEventQueryUnload(endpoint, params = {}) {
    const queryParams = this.buildQueryParams(params);
    const url = `${this.apiBaseUrl}/api/analytics/${endpoint}?${queryParams}`;

    try {
      // sendBeacon is preferred on unload because browsers allow it to complete in background.
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const queued = navigator.sendBeacon(url, '');
        this.incrementCounter('unload', queued);
        if (queued) {
          return { success: true, queued: true, transport: 'beacon' };
        }
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: this.buildHeaders(false),
        keepalive: true
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.incrementCounter('unload', true);
      try {
        return await response.json();
      } catch (_error) {
        return { success: true, transport: 'keepalive' };
      }
    } catch (error) {
      console.warn('Analytics unload tracking failed:', error.message);
      this.incrementCounter('unload', false);
      return null;
    }
  }

  // Track search queries
  async trackSearch(query, resultsCount = 0) {
    return this.trackEventQuery('track/search', {
      query,
      results_count: resultsCount
    });
  }

  // Track card views
  async trackCardView(cardId, cameFrom = 'direct', setId = null, viewDurationMs = null) {
    // Start timing for this card
    this.startTimer(`card_${cardId}`);

    if (this.deterministicViewTrackingEnabled) {
      return Promise.resolve({ success: true, deferred: true });
    }

    // If no duration provided, use 0 for initial view (we'll track exit separately)
    const duration = viewDurationMs !== null ? viewDurationMs : 0;

    return this.trackEventQuery('track/card-view', {
      card_id: cardId,
      came_from: cameFrom,
      set_id: setId,
      view_duration_ms: duration
    });
  }

  // Track set views (SMART DEDUPLICATION)
  async trackSetView(setId, viewDurationMs = null, cameFrom = 'direct') {
    if (this.deterministicViewTrackingEnabled) {
      this.startTimer(`set_${setId}`);
      return Promise.resolve({ success: true, deferred: true });
    }

    // Smart deduplication: Check if we've already tracked this set in this session
    const sessionKey = `set_viewed_${setId}`;
    const lastViewed = sessionStorage.getItem(sessionKey);
    const now = Date.now();

    // Only track if we haven't viewed this set in the last 30 seconds
    if (lastViewed && (now - parseInt(lastViewed)) < 30000) {
      console.log(`Set ${setId} already tracked recently, skipping duplicate`);
      return null;
    }

    // Store the current timestamp
    sessionStorage.setItem(sessionKey, now.toString());
    this.startTimer(`set_${setId}`);

    // If no duration provided, use 0 for initial view (we'll track exit separately)
    const duration = viewDurationMs !== null ? viewDurationMs : 0;

    console.log(`Tracking set view: ${setId}, duration: ${duration}ms`);
    return this.trackEventQuery('track/set-view', {
      set_id: setId,
      view_duration_ms: duration,
      came_from: cameFrom
    });
  }

  // Track series views
  async trackSeriesView(seriesId, viewDurationMs = null) {
    // Start timing for this series
    this.startTimer(`series_${seriesId}`);

    if (this.deterministicViewTrackingEnabled) {
      return Promise.resolve({ success: true, deferred: true });
    }

    // If no duration provided, use 0 for initial view (we'll track exit separately)
    const duration = viewDurationMs !== null ? viewDurationMs : 0;

    return this.trackEventQuery('track/series-view', {
      series_id: seriesId,
      view_duration_ms: duration
    });
  }

  // Track navigation from series page into a set card page (distinct from set_view)
  async trackSetNavigation(setId, seriesId = null, sourcePage = 'series_page') {
    return this.trackEventQuery('track/set-navigation', {
      set_id: setId,
      series_id: seriesId,
      source_page: sourcePage
    }, {
      useUnloadTransport: this.deterministicViewTrackingEnabled,
      transport: this.deterministicViewTrackingEnabled ? 'unload' : 'default'
    });
  }

  // Track marketplace clicks
  async trackMarketplaceClick(marketplace, cardId = null, listingId = null, price = null, metadata = {}) {
    const safeMetadata = metadata || {};
    return this.trackEventQuery('track/marketplace-click', {
      marketplace,
      channel: safeMetadata.channel,
      store_name: safeMetadata.store_name,
      card_id: cardId,
      listing_id: listingId || safeMetadata.listing_id,
      listing_url: safeMetadata.listing_url,
      price: price,
      condition: safeMetadata.condition
    });
  }

  // Track page views
  async trackPageView(pageUrl = null, referrer = null) {
    const url = pageUrl || window.location.pathname + window.location.search;
    const ref = referrer || document.referrer;
    
    return this.trackEventQuery('track/page-view', {
      page_url: url,
      referrer: ref
    });
  }

  // Track filter usage
  async trackFilter(filterType, filterValue) {
    return this.trackEventQuery('track/filter', {
      filter_type: filterType,
      filter_value: filterValue
    });
  }

  // Track user actions (for authenticated users)
  async trackUserAction(action, userId = null, metadata = {}) {
    if (!userId) return null;
    
    this.setUserId(userId);
    return this.trackEventQuery('track/authenticated-user', {
      event_type: action,
      metadata: JSON.stringify(metadata || {})
    });
  }

  // Helper: Track navigation with smart timing
  trackNavigation(fromPage, toPage, element = null) {
    const navigationData = {
      from_page: fromPage,
      to_page: toPage,
      element_clicked: element,
      navigation_time: Date.now()
    };
    
    // Store navigation context for the next page load
    sessionStorage.setItem('navigation_context', JSON.stringify(navigationData));
  }

  // Helper: Get navigation context
  getNavigationContext() {
    const context = sessionStorage.getItem('navigation_context');
    if (context) {
      sessionStorage.removeItem('navigation_context');
      return JSON.parse(context);
    }
    return null;
  }

  // Track set view exit with actual duration
  async trackSetViewExit(setId, cameFrom = 'direct') {
    const key = `set_${setId}`;
    if (this.sentViewExitKeys.has(key)) return null;

    const duration = this.getTimerDuration(key);
    if (duration <= 1000) return null;

    this.sentViewExitKeys.add(key);
    this.pageEntryTimes.delete(key);
    console.log(`Tracking set view exit: ${setId}, duration: ${duration}ms`);
    const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    return this.trackEventQuery('track/set-view', {
      set_id: setId,
      view_duration_ms: duration,
      came_from: cameFrom
    }, {
      useUnloadTransport: this.deterministicViewTrackingEnabled && isHidden,
      transport: isHidden ? 'unload' : 'default'
    });
  }

  // Track series view exit with actual duration
  async trackSeriesViewExit(seriesId) {
    const key = `series_${seriesId}`;
    if (this.sentViewExitKeys.has(key)) return null;

    const duration = this.getTimerDuration(key);
    if (duration <= 1000) return null;

    this.sentViewExitKeys.add(key);
    this.pageEntryTimes.delete(key);
    console.log(`Tracking series view exit: ${seriesId}, duration: ${duration}ms`);
    const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    return this.trackEventQuery('track/series-view', {
      series_id: seriesId,
      view_duration_ms: duration
    }, {
      useUnloadTransport: this.deterministicViewTrackingEnabled && isHidden,
      transport: isHidden ? 'unload' : 'default'
    });
  }

  // Track card view exit with actual duration
  async trackCardViewExit(cardId, setId = null, cameFrom = 'direct') {
    const key = `card_${cardId}`;
    if (this.sentViewExitKeys.has(key)) return null;

    const duration = this.getTimerDuration(key);
    if (duration <= 1000) return null;

    this.sentViewExitKeys.add(key);
    this.pageEntryTimes.delete(key);
    console.log(`Tracking card view exit: ${cardId}, duration: ${duration}ms`);
    const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    return this.trackEventQuery('track/card-view', {
      card_id: cardId,
      set_id: setId,
      came_from: cameFrom,
      view_duration_ms: duration
    }, {
      useUnloadTransport: this.deterministicViewTrackingEnabled && isHidden,
      transport: isHidden ? 'unload' : 'default'
    });
  }
}

// Create and export a singleton instance
const analyticsService = new FrontendAnalyticsService();

export default analyticsService;
